import { useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthId } from '@/hooks/useAuthId';
import {
  resolveQuizPersistenceIdentity,
  type QuizPersistenceIdentity,
} from '@/lib/auth/quizPersistenceIdentity';
import { getActiveRunIdentityState, useActiveRunIdentity } from '@/lib/auth/activeRunIdentity';
import type { QuizEditionId, QuizId } from '@/lib/domain/quiz/types';
import { toRpcIdentityParams } from '@/lib/auth/rpcIdentityParams';

export interface QuizCompletion {
  emoji_results: string;
  accuracy: number;
  total_questions: number;
  xp_earned: number;
  created_at: string;
}

const fetchQuizCompletion = async (
  editionId: QuizEditionId | undefined,
  userId: string | undefined,
  isAnonymous: boolean
): Promise<QuizCompletion | null> => {
  if (!editionId || !userId) return null;

  const { data, error } = await supabase
    .rpc('get_quiz_completion', {
      p_edition_id: editionId,
      ...toRpcIdentityParams({ userId, isAnonymous }),
    })
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('❌ Error fetching quiz completion', error);
    throw error;
  }

  return (data as QuizCompletion | null) ?? null;
};

export function useQuizCompletionQuery(
  quizId?: QuizId,
  editionId?: QuizEditionId,
  options?: { overrideUserId?: string; enabled?: boolean }
) {
  const { userId: authUserId, isAuthenticated } = useAuthId();
  const activeRunIdentity = useActiveRunIdentity();

  const resolvedUserId = options?.overrideUserId ?? authUserId;
  const queryIdentity =
    activeRunIdentity.status === 'ready'
      ? (activeRunIdentity.identity ??
        (resolvedUserId ? { userId: resolvedUserId, isAnonymous: !isAuthenticated } : null))
      : null;
  const queryEnabled =
    (options?.enabled ?? true) &&
    !!quizId &&
    !!editionId &&
    !!queryIdentity &&
    activeRunIdentity.status === 'ready';

  const identityBindingRef = useRef<{
    quizId?: QuizId;
    editionId?: QuizEditionId;
    generation: number;
    promise?: Promise<QuizPersistenceIdentity | null>;
  }>({ quizId, editionId, generation: activeRunIdentity.generation });

  if (
    identityBindingRef.current.quizId !== quizId ||
    identityBindingRef.current.editionId !== editionId ||
    identityBindingRef.current.generation !== activeRunIdentity.generation
  ) {
    identityBindingRef.current = { quizId, editionId, generation: activeRunIdentity.generation };
  }

  const getPersistenceIdentity = useCallback(async (): Promise<QuizPersistenceIdentity | null> => {
    const initialIdentityState = getActiveRunIdentityState();
    if (initialIdentityState.status !== 'ready') return null;

    const binding = identityBindingRef.current;
    if (!binding.promise) {
      const pendingIdentity = initialIdentityState.identity
        ? Promise.resolve(initialIdentityState.identity)
        : resolveQuizPersistenceIdentity(resolvedUserId);
      const boundIdentity = pendingIdentity.catch((error) => {
        if (identityBindingRef.current.promise === boundIdentity) {
          identityBindingRef.current.promise = undefined;
        }
        throw error;
      });
      binding.promise = boundIdentity;
    }
    const identity = await binding.promise;
    const currentIdentityState = getActiveRunIdentityState();
    if (
      currentIdentityState.status !== 'ready' ||
      currentIdentityState.generation !== initialIdentityState.generation
    ) {
      return null;
    }
    return identity;
  }, [resolvedUserId]);

  const completionQuery = useQuery<QuizCompletion | null>({
    queryKey: [
      'quizCompletion',
      quizId,
      editionId,
      queryIdentity?.userId ?? null,
      queryIdentity?.isAnonymous ?? false,
    ],
    queryFn: async () => {
      const identity = await getPersistenceIdentity();
      if (!identity) return null;
      return fetchQuizCompletion(editionId, identity.userId, identity.isAnonymous);
    },
    enabled: queryEnabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  return {
    completion: completionQuery.data,
    isLoadingCompletion: completionQuery.isLoading,
    completionError: completionQuery.error,
    refetchCompletion: completionQuery.refetch,
    canPersistCompletion: queryEnabled,
    getPersistenceIdentity,
  };
}
